import React, { useState, useEffect } from 'react';
import { X, Eye, Loader2, Database } from 'lucide-react';
import { LabelPlaceholder, LabelContext } from '../../../../../types/labelDesign';
import { useTranslation } from '../../../../../contexts/I18nProvider';

interface SampleDataDialogProps {
  isOpen: boolean;
  placeholders: LabelPlaceholder[];
  /** Ekstra plassholdere brukt i layout (f.eks. dotted table-kolonner som "Items.ItemNo"). */
  extraKeys?: string[];
  /**
   * Resolver-tabeller layouten refererer (utledet av LabelDesigner fra både
   * prikk-notasjon og alias-plassholdere som {{ItemNo}} → Items). Styrer hvilke
   * DB-kontekst-inputs som vises.
   */
  contextTables?: string[];
  /**
   * Datakilder for Table-elementer i layouten (dokumentmodus), f.eks. ["PListLines"].
   * Viser kontekst-seksjonen slik at forhåndsvisningen kan hente EKTE rader
   * (PListLines krever PList-ID); uten kontekst brukes genererte eksempelrader.
   */
  tableDataSources?: string[];
  initialData: Record<string, string>;
  onCancel: () => void;
  onSubmit: (data: Record<string, string>, context: LabelContext) => void;
  loading?: boolean;
}

export const SampleDataDialog: React.FC<SampleDataDialogProps> = ({
  isOpen,
  placeholders,
  extraKeys = [],
  contextTables = [],
  tableDataSources = [],
  initialData,
  onCancel,
  onSubmit,
  loading = false
}) => {
  const { t } = useTranslation();
  const [data, setData] = useState<Record<string, string>>(initialData);
  const [context, setContext] = useState<LabelContext>({});

  useEffect(() => {
    if (isOpen) {
      setData(initialData);
      setContext({});
    }
  }, [isOpen, initialData]);

  // Finn ut hvilke tabeller layouten referer til (for å vise relevante kontekst-inputs):
  // prikk-notasjon fra extraKeys + alias-utledede tabeller fra LabelDesigner
  const tablesInUse = React.useMemo(() => {
    const set = new Set<string>(contextTables);
    extraKeys.forEach((key) => {
      const dot = key.indexOf('.');
      if (dot > 0) set.add(key.slice(0, dot));
    });
    return set;
  }, [extraKeys, contextTables]);

  const hasPListLines = tableDataSources.includes('PListLines');
  const needsContext = tablesInUse.size > 0 || hasPListLines;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">{t('manager.modules.labelDesigner.sampleData.title', {}, 'Eksempeldata for forhåndsvisning')}</h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" disabled={loading}>
            <X size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <p className="text-xs text-gray-500 mb-2">
            {t('manager.modules.labelDesigner.sampleData.introBefore', {}, 'Verdiene under erstatter plassholdere som')}{' '}
            <code className="bg-gray-100 px-1">{`{{ItemNo}}`}</code>{' '}
            {t('manager.modules.labelDesigner.sampleData.introAfter', {}, 'i etiketten. Du kan justere hver enkelt før forhåndsvisning.')}
          </p>
          {groupByCategory(placeholders).map(([category, items]) => (
            <div key={category}>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{category}</div>
              <div className="space-y-1">
                {items.map((p) => (
                  <div key={p.key} className="grid grid-cols-3 gap-2 items-center">
                    <label className="text-sm text-gray-700 col-span-1" title={`{{${p.key}}}`}>
                      {p.label}
                    </label>
                    <input
                      className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={data[p.key] ?? ''}
                      onChange={(e) => setData({ ...data, [p.key]: e.target.value })}
                      placeholder={p.exampleValue}
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}

          {needsContext && (
            <div className="border border-blue-200 bg-blue-50 rounded p-3">
              <div className="flex items-center gap-2 text-xs font-semibold text-blue-900 uppercase mb-2">
                <Database size={14} /> {t('manager.modules.labelDesigner.sampleData.dbContextHeading', {}, 'DB-kontekst (henter reelle verdier)')}
              </div>
              <p className="text-xs text-blue-800 mb-3">
                {t('manager.modules.labelDesigner.sampleData.dbContextHint', {}, 'Fyll inn nøklene nedenfor for å hente faktiske rader fra databasen. Felter du lar stå tomme hentes ikke. Tabeller i bruk:')}{' '}
                <strong>{[...Array.from(tablesInUse), ...tableDataSources].join(', ')}</strong>
              </p>
              <div className="space-y-2">
                {hasPListLines && (
                  <CtxRow label="PList-ID">
                    <input
                      type="number"
                      className={ctxInputCls}
                      value={context.pListID ?? ''}
                      placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '99901' }, 'f.eks. 99901')}
                      title={t('manager.modules.labelDesigner.sampleData.pListIdHint', {}, 'Henter plukklistens faktiske linjer til tabellen. Tom = genererte eksempelrader.')}
                      onChange={(e) => setContext({ ...context, pListID: e.target.value ? parseInt(e.target.value) : undefined })}
                    />
                  </CtxRow>
                )}
                {(tablesInUse.has('Shipment') || tableDataSources.includes('ShipmentLines')) && (
                  <CtxRow label="Shipment-ID">
                    <input
                      type="number"
                      className={ctxInputCls}
                      value={context.shipmentID ?? ''}
                      placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '268140' }, 'f.eks. 268140')}
                      title={t('manager.modules.labelDesigner.sampleData.shipmentIdHint', {}, 'Henter forsendelsen, første ordre og alle ordrelinjene i forsendelsen (ShipmentLines). Tom = plukklistens egne ordrer hvis PList-ID er satt.')}
                      onChange={(e) => setContext({ ...context, shipmentID: e.target.value ? parseInt(e.target.value) : undefined })}
                    />
                  </CtxRow>
                )}
                {(tablesInUse.has('Items')) && (
                  <CtxRow label="Items.ItemNo">
                    <input className={ctxInputCls} value={context.itemNo ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': 'ART-12345' }, 'f.eks. ART-12345')}
                      onChange={(e) => setContext({ ...context, itemNo: e.target.value || undefined })} />
                  </CtxRow>
                )}
                {(tablesInUse.has('Orders') || tablesInUse.has('OrderLine')) && (
                  <>
                    <CtxRow label="Orders.OrderNo">
                      <input type="number" className={ctxInputCls} value={context.orderNo ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '10045' }, 'f.eks. 10045')}
                        onChange={(e) => setContext({ ...context, orderNo: e.target.value ? parseInt(e.target.value) : undefined })} />
                    </CtxRow>
                    {tablesInUse.has('OrderLine') && (
                      <CtxRow label="OrderLine.LineNo">
                        <input type="number" className={ctxInputCls} value={context.orderLineNo ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '1' }, 'f.eks. 1')}
                          onChange={(e) => setContext({ ...context, orderLineNo: e.target.value ? parseInt(e.target.value) : undefined })} />
                      </CtxRow>
                    )}
                  </>
                )}
                {(tablesInUse.has('POrders') || tablesInUse.has('POrderLine')) && (
                  <>
                    <CtxRow label="POrders.OrderNo">
                      <input type="number" className={ctxInputCls} value={context.pOrderNo ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '50021' }, 'f.eks. 50021')}
                        onChange={(e) => setContext({ ...context, pOrderNo: e.target.value ? parseInt(e.target.value) : undefined })} />
                    </CtxRow>
                    {tablesInUse.has('POrderLine') && (
                      <CtxRow label="POrderLine.LineNo">
                        <input type="number" className={ctxInputCls} value={context.pOrderLineNo ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '1' }, 'f.eks. 1')}
                          onChange={(e) => setContext({ ...context, pOrderLineNo: e.target.value ? parseInt(e.target.value) : undefined })} />
                      </CtxRow>
                    )}
                  </>
                )}
                {tablesInUse.has('StcBin') && (
                  <>
                    <CtxRow label="StcBin.WarehouseID">
                      <input className={ctxInputCls} value={context.warehouseID ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '1' }, 'f.eks. 1')}
                        onChange={(e) => setContext({ ...context, warehouseID: e.target.value.trim() || undefined })} />
                    </CtxRow>
                    <CtxRow label="StcBin.BinCode">
                      <input className={ctxInputCls} value={context.binCode ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': 'A-01-02-03' }, 'f.eks. A-01-02-03')}
                        onChange={(e) => setContext({ ...context, binCode: e.target.value || undefined })} />
                    </CtxRow>
                  </>
                )}
                {tablesInUse.has('OrderSSCC') && (
                  <CtxRow label="OrderSSCC.SSCC">
                    <input className={ctxInputCls} value={context.sscc ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '370800012000127140' }, 'f.eks. 370800012000127140')}
                      title={t('manager.modules.labelDesigner.sampleData.ssccHint', {}, 'Henter pallens rad + kjeder videre til Orders/OrderLine for samme pall.')}
                      onChange={(e) => setContext({ ...context, sscc: e.target.value || undefined })} />
                  </CtxRow>
                )}
                {/* PList-kontekst: PListSSCC, PList-hodet, og kjeden PListOrders → Orders (+ Shipment).
                    Dokumentmaler kan dermed la Orders.OrderNo stå tom og bare oppgi PList-ID. */}
                {(tablesInUse.has('PListSSCC') || tablesInUse.has('PList') || tablesInUse.has('PListOrders') || tablesInUse.has('Shipment')) && !hasPListLines && (
                  <CtxRow label="PList-ID">
                    <input type="number" className={ctxInputCls} value={context.pListID ?? ''} placeholder={t('manager.modules.labelDesigner.sampleData.example', { '0': '99901' }, 'f.eks. 99901')}
                      title={t('manager.modules.labelDesigner.sampleData.pListSsccHint', {}, 'Henter plukklistens hode, aktive SSCC (PListSSCC) og første ordre (PListOrders → Orders, + Shipment når koblet).')}
                      onChange={(e) => setContext({ ...context, pListID: e.target.value ? parseInt(e.target.value) : undefined })} />
                  </CtxRow>
                )}
              </div>
            </div>
          )}

          {extraKeys.length > 0 && (
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase mb-1">{t('manager.modules.labelDesigner.sampleData.overrideHeading', {}, 'Overstyr tabell-verdier (valgfritt)')}</div>
              <p className="text-xs text-gray-400 mb-2 italic">
                {t('manager.modules.labelDesigner.sampleData.overrideHint', {}, 'Tomme felter bruker DB-verdien fra kontekst ovenfor. Fyll inn for å overstyre.')}
              </p>
              <div className="space-y-1">
                {extraKeys.map((key) => (
                  <div key={key} className="grid grid-cols-3 gap-2 items-center">
                    <label className="text-sm text-gray-700 col-span-1 font-mono text-xs" title={`{{${key}}}`}>
                      {key}
                    </label>
                    <input
                      className="col-span-2 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={data[key] ?? ''}
                      onChange={(e) => setData({ ...data, [key]: e.target.value })}
                      placeholder={t('manager.modules.labelDesigner.sampleData.usesDbValue', {}, '(bruker DB-verdi)')}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            type="button"
            onClick={() => onSubmit(data, context)}
            disabled={loading}
            className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Eye size={14} />}
            {t('manager.modules.labelDesigner.sampleData.generatePdf', {}, 'Generer PDF')}
          </button>
        </div>
      </div>
    </div>
  );
};

function groupByCategory(placeholders: LabelPlaceholder[]): Array<[string, LabelPlaceholder[]]> {
  const map = new Map<string, LabelPlaceholder[]>();
  placeholders.forEach((p) => {
    const existing = map.get(p.category) || [];
    existing.push(p);
    map.set(p.category, existing);
  });
  return Array.from(map.entries());
}

const ctxInputCls =
  'col-span-2 px-2 py-1 border border-blue-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500';

const CtxRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="grid grid-cols-3 gap-2 items-center">
    <label className="text-xs text-blue-900 font-mono col-span-1">{label}</label>
    {children}
  </div>
);
